import { essayService } from "@/api/services/essay.service";

import { pushSubscriptionService } from "@/api/services/push-subscription.service";
import {
  createEssayInputValidator,
  deleteEssayInputValidator,
  essayIdValidator,
  publishEssayInputValidator,
  updateEssayInputValidator,
} from "@/api/validator/essay";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { authMiddlewareServer } from "../middleware/auth";

// PUBLIC ENDPOINTS

/**
 * List all published essays (public)
 */
export const listPublishedEssaysFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const essays = await essayService.listPublishedEssays();

    return {
      status: 200,
      body: {
        essays,
        total: essays.length,
      },
    };
  },
);

export const listPublishedEssaysOptions = () =>
  queryOptions({
    queryKey: ["listPublishedEssays"],
    queryFn: () => listPublishedEssaysFn(),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

/**
 * Get published essay by ID (public)
 */
export const getPublishedEssayByIdFn = createServerFn({ method: "GET" })
  .inputValidator(essayIdValidator)
  .handler(async ({ data }) => {
    const essay = await essayService.getPublishedEssayById(data.essayId);

    if (!essay) {
      return {
        status: 404,
        body: { message: "Essay not found or not published" },
      };
    }

    return {
      status: 200,
      body: essay,
    };
  });

export const getPublishedEssayByIdOptions = (essayId: string) =>
  queryOptions({
    queryKey: ["getPublishedEssayById", essayId],
    queryFn: () => getPublishedEssayByIdFn({ data: { essayId } }),
  });

// ADMIN ENDPOINTS (require admin authentication)

/**
 * List all essays including unpublished (admin)
 */
export const listAllEssaysFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .handler(async ({ context }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const essays = await essayService.listAllEssays();

    return {
      status: 200,
      body: {
        essays,
        total: essays.length,
      },
    };
  });

export const listAllEssaysOptions = () =>
  queryOptions({
    queryKey: ["listAllEssays"],
    queryFn: () => listAllEssaysFn(),
  });

/**
 * Get essay by ID (admin)
 */
export const getEssayByIdFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .inputValidator(essayIdValidator)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const essay = await essayService.getEssayById(data.essayId);

    if (!essay) {
      return {
        status: 404,
        body: { message: "Essay not found" },
      };
    }

    return {
      status: 200,
      body: essay,
    };
  });

export const getEssayByIdOptions = (essayId: string) =>
  queryOptions({
    queryKey: ["getEssayById", essayId],
    queryFn: () => getEssayByIdFn({ data: { essayId } }),
  });

/**
 * Create new essay (admin)
 */
export const createEssayFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(createEssayInputValidator)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const essay = await essayService.createEssay(data, context.auth.userId);

    return {
      status: 201,
      body: {
        message: "Essay created successfully",
        essay,
      },
    };
  });

/**
 * Update existing essay (admin)
 */
export const updateEssayFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(updateEssayInputValidator)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const { essayId, ...updateData } = data;
    const essay = await essayService.updateEssay(essayId, updateData);

    if (!essay) {
      return {
        status: 404,
        body: { message: "Essay not found" },
      };
    }

    return {
      status: 200,
      body: {
        message: "Essay updated successfully",
        essay,
      },
    };
  });

/**
 * Delete essay (admin)
 */
export const deleteEssayFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(deleteEssayInputValidator)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const essay = await essayService.deleteEssay(data.essayId);

    if (!essay) {
      return {
        status: 404,
        body: { message: "Essay not found" },
      };
    }

    return {
      status: 200,
      body: {
        message: "Essay deleted successfully",
      },
    };
  });

/**
 * Publish essay (admin)
 */
export const publishEssayFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(publishEssayInputValidator)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const essay = await essayService.publishEssay(data.essayId);

    if (!essay) {
      return {
        status: 404,
        body: { message: "Essay not found" },
      };
    }

    void pushSubscriptionService.sendNotificationToAllFiltered(
      "notifNewContent",
      {
        title: "Yeni Yazı",
        body: essay.title,
        url: `/essay/${essay.essayId}`,
      },
    );

    return {
      status: 200,
      body: {
        message: "Essay published successfully",
        essay,
      },
    };
  });

/**
 * Unpublish essay (admin)
 */
export const unpublishEssayFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(publishEssayInputValidator)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const essay = await essayService.unpublishEssay(data.essayId);

    if (!essay) {
      return {
        status: 404,
        body: { message: "Essay not found" },
      };
    }

    return {
      status: 200,
      body: {
        message: "Essay unpublished successfully",
        essay,
      },
    };
  });

/**
 * Toggle publish status (admin)
 */
export const togglePublishEssayFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(publishEssayInputValidator)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const essay = await essayService.togglePublish(data.essayId);

    if (!essay) {
      return {
        status: 404,
        body: { message: "Essay not found" },
      };
    }

    if (essay.isPublished) {
      void pushSubscriptionService.sendNotificationToAllFiltered(
        "notifNewContent",
        {
          title: "Yeni Yazı",
          body: essay.title,
          url: `/essay/${essay.essayId}`,
        },
      );
    }

    return {
      status: 200,
      body: {
        message: `Essay ${
          essay.isPublished ? "published" : "unpublished"
        } successfully`,
        essay,
      },
    };
  });
